import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMatchEventsSocket } from '../../hooks/useMatchEventsSocket';
import { useMatchEventsHistory } from '../../api/useMatchEvents';
import {
  deriveMatchSummary,
  mergeHistoryAndLive,
  secondsToClock,
} from '../../utils/matchEventsFeed';
import {
  matchStateFromSummary,
  formatStateLine,
  groupBySubgroup,
} from '../../utils/sniperArm';
import {
  useActivateSniperEvent,
  useDeactivateSniperEvent,
  usePatchSniperMarket,
  useDeleteSniperMarket,
} from '../../api/useSniper';
import type { SniperEvent } from '../../types/sniper.types';
import { SniperMarketRow } from './SniperMarketRow';
import { SniperImportDialog } from './SniperImportDialog';

/**
 * One event (match) card: the live state line (score + `Corners H–A` + clock,
 * derived from the SAME match-events feed the read-only tab uses), an Active
 * toggle (reuses the `/match-events` feed active set), an import-by-link
 * control, and the event's markets subgrouped by trigger type (Gol / Corner).
 * The live `MatchState` drives each row's ARMED badge.
 */
export function SniperEventCard({ event }: { event: SniperEvent }) {
  const [importOpen, setImportOpen] = useState(false);

  const activate = useActivateSniperEvent();
  const deactivate = useDeactivateSniperEvent();
  const patchMarket = usePatchSniperMarket();
  const deleteMarket = useDeleteSniperMarket();

  // Only an ACTIVE event streams — subscribing to an inactive fixture joins a
  // room with no feed attached.
  const subscribeFixtureId = event.active ? event.fixtureId : null;
  const { frames: liveFrames } = useMatchEventsSocket({
    fixtureId: subscribeFixtureId,
  });
  const { data: history = [] } = useMatchEventsHistory(subscribeFixtureId);

  const frames = useMemo(
    () => mergeHistoryAndLive(history, liveFrames),
    [history, liveFrames],
  );
  const summary = useMemo(() => deriveMatchSummary(frames), [frames]);
  const state = useMemo(
    () => (event.active ? matchStateFromSummary(summary, frames) : null),
    [event.active, summary, frames],
  );

  const clockText = secondsToClock(state?.clock ?? null);
  const stateLine = state
    ? formatStateLine(state, event.homeTeam, event.awayTeam, clockText)
    : `${event.homeTeam} vs ${event.awayTeam}`;

  const subgroups = useMemo(
    () => groupBySubgroup(event.markets ?? []),
    [event.markets],
  );

  const toggleActive = (next: boolean) => {
    if (next) activate.mutate(event.fixtureId);
    else deactivate.mutate(event.fixtureId);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 600 }}>
          {stateLine}
        </Typography>
        {event.league && (
          <Chip size="small" variant="outlined" label={event.league} />
        )}
        {event.live && <Chip size="small" color="success" label="live" />}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import markets
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={event.active}
              onChange={(e) => toggleActive(e.target.checked)}
              inputProps={{ 'aria-label': 'activate event feed' }}
            />
          }
          label={event.active ? 'Active' : 'Inactive'}
        />
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {subgroups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No markets yet — use “Import markets” to add some.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {subgroups.map((group) => (
            <Box key={group.subgroup}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                {group.label}
              </Typography>
              <Stack spacing={0.75}>
                {group.rows.map((market) => (
                  <SniperMarketRow
                    key={market._id}
                    market={market}
                    state={state}
                    busy={patchMarket.isLoading || deleteMarket.isLoading}
                    onPatch={(patch) =>
                      patchMarket.mutate({ marketId: market._id, patch })
                    }
                    onDelete={() => deleteMarket.mutate(market._id)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <SniperImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        fixtureId={event.fixtureId}
        homeTeam={event.homeTeam}
        awayTeam={event.awayTeam}
      />
    </Paper>
  );
}
